import {useEffect, useState} from 'react'
import {useHeaderContext} from '../../../../contexts/header-context'

/* Distance (px) we want absolutely-positioned tile controls (tooltip,
 * video play/pause, etc.) to sit BELOW the header's visible bottom when
 * the header is transparent and the tile lives behind it. Matches the
 * authored `base`/`lg` defaults already used by those controls (16px on
 * mobile, 24px on desktop). */
const CLEARANCE_PX_BASE = 16
const CLEARANCE_PX_LG = 24
/* `lg` breakpoint mirrors Chakra's default (62em ≈ 992px) which is also
 * what the rest of the slider's responsive styles assume. Read via
 * `matchMedia` so we don't have to pull a `ChakraProvider` (and its
 * theme) into this small utility hook for tests to render. */
const LG_MEDIA_QUERY = '(min-width: 62em)'

// A header inside a fixed/sticky wrapper has a viewport-stable rect, so adding scrollY
// would make the clearance grow as the page scrolls.
const isViewportAnchored = (node) => {
    if (typeof window.getComputedStyle !== 'function') return false
    for (let el = node; el instanceof Element && el !== document.body; el = el.parentElement) {
        const {position} = window.getComputedStyle(el)
        if (position === 'fixed' || position === 'sticky') return true
    }
    return false
}

/**
 * Compute an effective top-offset (px) for an absolutely-positioned control
 * anchored inside a tile that may render behind a transparent (overlay)
 * header.
 *
 * The decision is made purely from geometry, NOT from the header variant:
 *  - Transparent header → `position: absolute, top: 0` so it does NOT take
 *    layout space; tiles below render at the page-top region → the header's
 *    visible bottom is BELOW the tile's top → we push the control down so
 *    its top sits 24px under the header's visible bottom.
 *  - Opaque header → in normal flow, takes layout space; tiles render below
 *    its bottom edge → element top ≥ header bottom → returns `null` so the
 *    authored default applies.
 *
 * Going purely by geometry also handles cases where transparency is decided
 * by CMS `HeaderVariations` rather than the env-var-driven context variant
 * (`useTransparentHeader` resolves an effective variant that the context
 * doesn't expose).
 *
 * `tileRef` should point to the nearest positioning ancestor that contains
 * the control (otherwise `getBoundingClientRect` returns the wrong element's
 * coordinates and the math is off).
 *
 * Returns `null` when no override is needed; callers should fall back to
 * their authored default in that case.
 */
export const useHeaderClearance = (tileRef) => {
    const {headerHeight, headerRef} = useHeaderContext() || {}
    const [topPx, setTopPx] = useState(null)

    useEffect(() => {
        if (!tileRef?.current || typeof window === 'undefined') {
            setTopPx(null)
            return undefined
        }

        /* Track the `lg` breakpoint so the clearance value stays in sync
         * with the authored `{base: '16px', lg: '24px'}` defaults across
         * viewport resizes (e.g. devtools, orientation change). */
        const mql =
            typeof window.matchMedia === 'function'
                ? window.matchMedia(LG_MEDIA_QUERY)
                : null
        const getClearance = () =>
            mql?.matches ? CLEARANCE_PX_LG : CLEARANCE_PX_BASE

        const compute = () => {
            const node = tileRef.current
            if (!node || !headerHeight) {
                setTopPx(null)
                return
            }
            const rect = node.getBoundingClientRect()
            const elementTopDoc = rect.top + window.scrollY
            /* Compute the header's actual document-bottom position from
             * the header DOM node when available. Anything else (e.g.
             * announcement bars in normal flow above the header) shifts
             * the header DOWN in document coordinates, so a naive
             * `headerHeight`-only threshold under-counts the band that
             * the header visually overlaps. Falling back to
             * `headerHeight` keeps the legacy behaviour when the ref
             * isn't wired yet. */
            const headerNode = headerRef?.current
            const headerBottomDoc = headerNode
                ? headerNode.getBoundingClientRect().bottom +
                  (isViewportAnchored(headerNode) ? 0 : window.scrollY)
                : headerHeight

            /* Element already sits BELOW the header's visible bottom —
             * the authored default offset is fine, no override needed. */
            if (elementTopDoc >= headerBottomDoc) {
                setTopPx(null)
                return
            }
            /* Push the control so its top sits `getClearance()` below
             * the header's visible bottom. The value is the distance
             * from the element's top (the `top` origin of the absolutely-
             * positioned control) to that target line. */
            setTopPx(headerBottomDoc - elementTopDoc + getClearance())
        }

        /* Schedule the first measurement on the next frame so layout has
         * had a chance to settle (the carousel parent flips between Grid
         * and Carousel modes via CSS, and the video block swaps responsive
         * sources on mount). */
        const raf = window.requestAnimationFrame(compute)
        window.addEventListener('resize', compute)
        /* `resize` already fires on most viewport changes that flip the
         * breakpoint, but `matchMedia.change` covers the edge cases
         * (e.g. DPI changes, devtools docking) and keeps the clearance
         * in sync without needing to read the breakpoint on every
         * resize tick. */
        mql?.addEventListener?.('change', compute)

        /* Re-measure when the tile itself, or layout above it, changes
         * size. Observing `document.body` catches lazy-loaded media above
         * the tile pushing it down; observing the tile catches its own
         * intrinsic-size changes (e.g. responsive media switches). */
        let ro
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(compute)
            ro.observe(tileRef.current)
            if (document.body) ro.observe(document.body)
        }

        return () => {
            window.cancelAnimationFrame(raf)
            window.removeEventListener('resize', compute)
            mql?.removeEventListener?.('change', compute)
            ro?.disconnect()
        }
    }, [headerHeight, headerRef, tileRef])

    return topPx
}
